import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  X,
  Info,
  ClipboardList,
  FilePlus,
  Search,
  PencilLine,
  BarChart2,
  FileText,
} from 'lucide-react';
import '../CSS/GuideModal.css';

const GradesGuideModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) setIsOpen(false);
  };

  return (
    <>
      {/* ── Floating Action Button ── */}
      <div className="gml-fab-wrapper">
        <span className="gml-fab-tooltip">Need help?</span>
        <button
          className="gml-fab"
          onClick={() => setIsOpen(true)}
          aria-label="Open Grades Guide"
          title="Grades Guide"
        >
          <BookOpen className="gml-fab-icon" />
        </button>
      </div>

      {/* ── Modal ── */}
      {isOpen && (
        <div
          className="gml-backdrop"
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="gml-modal-title"
        >
          <div className="gml-modal">

            {/* Header */}
            <div className="gml-modal-header">
              <div className="gml-modal-title-wrap">
                <BookOpen className="gml-modal-header-icon" />
                <h2 className="gml-modal-title" id="gml-modal-title">
                  Academic Reports Module Guide
                </h2>
              </div>
              <button
                className="gml-modal-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close guide"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="gml-modal-body">

              {/* Welcome Banner */}
              <div className="gml-welcome-banner">
                <Info className="gml-welcome-icon" />
                <div>
                  <p className="gml-welcome-heading">
                    Welcome to the Academic Reports Module!
                  </p>
                  <p className="gml-welcome-text">
                    This comprehensive guide will help you navigate and utilize
                    all features of the Grades component effectively. Use it
                    anytime you need a quick reference.
                  </p>
                </div>
              </div>

              {/* ── Key Features Overview ── */}
              <h3 className="gml-section-heading">Key Features Overview</h3>
              <div className="gml-cards-grid">

                {/* Recorded Grades */}
                <div className="gml-feature-card gml-feature-card--blue">
                  <div className="gml-card-head gml-card-head--blue">
                    <ClipboardList className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Recorded Grades</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">1</span>
                        View all recorded academic results
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">2</span>
                        Advanced filtering by class and search
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">3</span>
                        Edit or delete existing grade entries
                      </li>
                    </ul>
                  </div>
                </div>

                {/* New Entries */}
                <div className="gml-feature-card gml-feature-card--green">
                  <div className="gml-card-head gml-card-head--green">
                    <FilePlus className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">New Entries</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">1</span>
                        Add new academic records for students
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">2</span>
                        Comprehensive subject and pace management
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">3</span>
                        Automatic grade and GPA calculation
                      </li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Detailed Feature Guide ── */}
              <h3 className="gml-section-heading">Detailed Feature Guide</h3>
              <div className="gml-detail-section">

                {/* Working with Recorded Grades */}
                <div className="gml-detail-card gml-detail-card--cyan">
                  <div className="gml-detail-head gml-detail-head--cyan">
                    <Search className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Working with Recorded Grades</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--cyan">Search and Filter</p>
                    <ul className="gml-detail-list">
                      <li>Use the search bar to find grades by student name, subject, or percentage</li>
                      <li>Filter by class using the dropdown to view only specific class results</li>
                      <li>Sort results by student name or class in ascending / descending order</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--cyan">Student Actions</p>
                    <ul className="gml-detail-list">
                      <li><strong>Hover over student names</strong> to reveal the action menu</li>
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--success">View on Graph</span>
                        — Visualize student performance
                      </li>
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--muted">View Grade Summary</span>
                        — Detailed academic summary
                      </li>
                      <li><strong>Generate Transcript</strong> — Available for classes 10 &amp; 12 (PDF)</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--cyan">Editing Grades</p>
                    <ul className="gml-detail-list">
                      <li>Click the edit (pencil) button to modify a grade entry</li>
                      <li>Modify pace numbers, scores, subject level, or year</li>
                      <li>Percentage and letter grade update automatically based on pace scores</li>
                    </ul>
                  </div>
                </div>

                {/* Adding New Academic Records */}
                <div className="gml-detail-card gml-detail-card--yellow">
                  <div className="gml-detail-head gml-detail-head--yellow">
                    <PencilLine className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Adding New Academic Records</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--yellow">Student Selection</p>
                    <ul className="gml-detail-list">
                      <li>Search students by name or filter by class</li>
                      <li>Sort the student list for easier navigation</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--yellow">Subject Entry</p>
                    <ul className="gml-detail-list">
                      <li>Select a subject from the dropdown (includes credit information)</li>
                      <li>Set the subject level (default is 1)</li>
                      <li>Enter the academic year (defaults to current year)</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--yellow">Pace Management</p>
                    <ul className="gml-detail-list">
                      <li>Add multiple pace numbers and corresponding scores</li>
                      <li>Click <strong>+</strong> to add new pace fields (max 12)</li>
                      <li>Click <strong>×</strong> to remove pace fields</li>
                      <li>Percentage, letter grade, and GPA calculate automatically</li>
                      <li>Duplicate pace numbers are automatically prevented</li>
                    </ul>
                  </div>
                </div>

                {/* Advanced Features */}
                <div className="gml-detail-card gml-detail-card--green">
                  <div className="gml-detail-head gml-detail-head--green">
                    <BarChart2 className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Advanced Features</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--green">Grade Visualization</p>
                    <ul className="gml-detail-list">
                      <li>Interactive bar charts showing subject performance</li>
                      <li>Hover over bars to see detailed grade information</li>
                      <li>Subject details listed below the graph</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--green">Transcript Generation</p>
                    <ul className="gml-detail-list">
                      <li>Available only for classes 10 and 12</li>
                      <li>Professional PDF format with school branding</li>
                      <li>Includes all subjects with pace numbers and scores</li>
                      <li>Calculates total credits and GPA where applicable</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--green">Grade Summary</p>
                    <ul className="gml-detail-list">
                      <li>Comprehensive overview of a student's academic performance</li>
                      <li>Includes GPA calculation details for classes 10 &amp; 12</li>
                      <li>Printable format with all subject details</li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Best Practices ── */}
              <div className="gml-best-practices">
                <p className="gml-best-practices-heading">Tips for Effective Use</p>
                <ul className="gml-best-practices-list">
                  {[
                    'Pay attention to toast notifications for system feedback.',
                    'For classes 10 & 12, GPA is automatically calculated based on the standard scale.',
                    'Duplicate pace numbers are automatically prevented — no need to check manually.',
                    'Use the search and filter together to quickly narrow down large grade lists.',
                    'Export transcripts after each term to keep printed records up to date.',
                  'Access this guide anytime via the floating button on the bottom-right of the screen.',
                  ].map((tip, i) => (
                    <li key={i}>
                      <span className="gml-bp-dot" aria-hidden="true" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

            </div>

            {/* Footer */}
            <div className="gml-modal-footer">
              <button className="gml-close-btn" onClick={() => setIsOpen(false)}>
                Got it
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default GradesGuideModal;